import cn from "classnames";
import BookForm from "components/shared/book/form";
import Loader from "components/shared/loader";
import { parallelItemChangeModalTitle } from "helpers/bid";
import {
    forwardingOrderChangeBehavioursEnum,
    organizationFlags,
    organizationFlagsEnum,
    parallelPointBehaviorsEnum,
} from "helpers/enums";
import { serializeReq, validateForm } from "helpers/form";
import { paths } from "helpers/paths";
import { flagVal } from "helpers/system";
import cloneDeep from "lodash/cloneDeep";
import pick from "lodash/pick";
import moment from "moment";
import React, { lazy, Suspense } from "react";
import { getBidForItemChangePointVisualization } from "sources/bidItemChanges";
import {
    applyItemChangePoints,
    checkForwardingOrderCarHasChanged,
    checkItemsBeforeParallelPoint,
    getBidsForParallelPoint,
} from "sources/books/bids";
import AddressPicker from "./addressPicker";
import BidsForParallelPoint from "./bidsForParallelPoint";
import { config, initialState } from "./config";
import ReplacementForm from "./replacementForm";

const TimelineForVisualization = lazy(() => import("./timelineForVisualization/index.js"));

const parallelItemChangePointsFlagValue = flagVal(organizationFlags, organizationFlagsEnum.ParallelItemChangePoints);
const requiredFieldsForParallelChange = {
    and: ["planEnterDate", "mapObject"],
    or: ["shiftDriverId", "shiftSecondaryDriverId", "shiftCarId", "shiftTrailerId"],
};
const fieldsForVisualization = ["shiftCarId", "planEnterDate"];
const fieldsForParallelChange = [
    "planEnterDate",
    "planLeaveDate",
    "mapObject",
    "shiftDriverId",
    "shiftSecondaryDriverId",
    "shiftCarId",
    "shiftTrailerId",
];

const createKeyExtension = () => `new-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const createEmptyBidItemChangePoint = () => ({
    ...initialState,
    keyExtension: createKeyExtension(),
    mapObject: null,
    shiftCar: null,
});

export const isValidBidItemChangePoint = (item) => {
    const serialized = serializeReq(item);
    return (
        validateForm(config, serialized) &&
        !!serialized.planEnterDate &&
        !!serialized.mapObject?.address &&
        (requiredFieldsForParallelChange.or.some((key) => !!serialized[key]) || !!serialized.shouldClearSecondaryDriver)
    );
};

export const formatBidItemChangePoint = ({ shouldClearSecondaryDriver, ...item }) => {
    const serialized = pick(cloneDeep(serializeReq(item)), Object.keys(config));
    if (!serialized.id) {
        delete serialized.id;
    }
    requiredFieldsForParallelChange.or.forEach((field) => {
        if (serialized[field] === null) {
            delete serialized[field];
        }
    });
    if (shouldClearSecondaryDriver) {
        serialized.shiftSecondaryDriverId = null;
        serialized.changeFlag = "shouldClearSecondaryDriver";
    }
    serialized.planEnterDate = moment(serialized.planEnterDate).format("YYYY-MM-DDTHH:mm");
    if (serialized.planLeaveDate) {
        serialized.planLeaveDate = moment(serialized.planLeaveDate).format("YYYY-MM-DDTHH:mm");
    }
    return serialized;
};

const hasClearSecondaryDriverFlag = (item) =>
    (item?.changeFlag || "").toLowerCase().includes("shouldclearsecondarydriver");

const getResourceId = (resource) => resource?.id ?? resource?.carId ?? resource?.trailerId;

const itemTypeToCompositionKey = {
    Car: "car",
    Driver: "driver",
    SecondaryDriver: "secondaryDriver",
    Trailer: "trailer",
};

const getResourceFromItemChange = ({ displayName, itemId, itemType }) => {
    if (["Driver", "SecondaryDriver"].includes(itemType)) {
        return { id: itemId, shortName: displayName };
    }
    return { id: itemId, number: displayName };
};

const getItemChangesByStartedAt = (itemChangesHistory = []) => {
    const changesByStartedAt = itemChangesHistory.reduce((result, itemChange) => {
        if (itemChange.startedAt) {
            if (!result[itemChange.startedAt]) {
                result[itemChange.startedAt] = [];
            }
            result[itemChange.startedAt].push(itemChange);
        }
        return result;
    }, {});

    return Object.entries(changesByStartedAt).sort(
        ([firstStartedAt], [secondStartedAt]) => moment(firstStartedAt).valueOf() - moment(secondStartedAt).valueOf(),
    );
};

const getCompletedPointComposition = (bid, item, itemChangesHistory) => {
    const pointStartedAt = item?.autoEnteredAt || item?.manualStartAt;
    if (!pointStartedAt || !itemChangesHistory?.length) {
        return null;
    }
    const pointStartedAtValue = moment(pointStartedAt).valueOf();

    const composition = {
        car: bid?.car,
        driver: bid?.driver,
        secondaryDriver: bid?.secondaryDriver,
        trailer: bid?.trailer,
    };

    for (const [startedAt, itemChanges] of getItemChangesByStartedAt(itemChangesHistory)) {
        if (moment(startedAt).valueOf() >= pointStartedAtValue) {
            return composition;
        }

        itemChanges.forEach((itemChange) => {
            const compositionKey = itemTypeToCompositionKey[itemChange.itemType];
            if (compositionKey) {
                composition[compositionKey] = getResourceFromItemChange(itemChange);
            }
        });
    }

    return composition;
};

const resolveCurrentComposition = (bid, items = [], item, itemChangesHistory) => {
    const completedPointComposition = getCompletedPointComposition(bid, item, itemChangesHistory);
    if (completedPointComposition) {
        return completedPointComposition;
    }

    const composition = {
        car: bid?.car,
        driver: bid?.driver,
        secondaryDriver: bid?.secondaryDriver,
        trailer: bid?.trailer,
    };
    const sortedItems = [...items].sort(
        (a, b) => moment(a.planEnterDateOffset).valueOf() - moment(b.planEnterDateOffset).valueOf(),
    );

    for (const itemToApply of sortedItems) {
        if (itemToApply.id === item?.id) {
            break;
        }
        if (itemToApply.shiftCar) {
            composition.car = itemToApply.shiftCar;
        }
        if (itemToApply.shiftDriver) {
            composition.driver = itemToApply.shiftDriver;
        }
        if (itemToApply.shiftSecondaryDriver) {
            composition.secondaryDriver = itemToApply.shiftSecondaryDriver;
        }
        if (itemToApply.shiftTrailer) {
            composition.trailer = itemToApply.shiftTrailer;
        }
        if (hasClearSecondaryDriverFlag(itemToApply)) {
            composition.secondaryDriver = null;
        }
    }

    return composition;
};

export const runBidItemChangePointChecks = ({
    add,
    bid,
    bidId,
    firstBidPointEntered,
    hasParallelBidId,
    item,
    pointToApply,
}) => {
    const serialized = pointToApply || formatBidItemChangePoint(item);
    const promises = [];
    if (bidId && !serialized.parallelBidId && hasParallelBidId && !firstBidPointEntered) {
        promises.push(() =>
            checkItemsBeforeParallelPoint({ bidId, pointToApply: serialized }).then(
                ({ willBeChanged, parallelBidId, car, driver, trailer }) => {
                    return new Promise((resolve) => {
                        if (willBeChanged) {
                            add({
                                name: "parallelBidChange",
                                windowMod: "sm",
                                title: parallelItemChangeModalTitle(car, driver, trailer),
                                parallelBidId,
                                withoutEscClose: true,
                                actions: {
                                    update: () => resolve({ parallelPointBehavior: parallelPointBehaviorsEnum.Update }),
                                    delete: () => resolve({ parallelPointBehavior: parallelPointBehaviorsEnum.Delete }),
                                    cancel: () => resolve(false),
                                },
                            });
                        } else {
                            resolve({});
                        }
                    });
                },
            ),
        );
    }
    if (bidId && bid?.forwardingOrder) {
        promises.push(() =>
            checkForwardingOrderCarHasChanged({
                bidId,
                pointToApply: serialized,
            }).then(({ willBeChanged }) => {
                return new Promise((resolve) => {
                    if (willBeChanged) {
                        add({
                            name: "forwardingOrderCarChange",
                            windowMod: "sm",
                            title: "Обратите внимание",
                            withoutEscClose: true,
                            actions: {
                                update: () =>
                                    resolve({
                                        forwardingOrderCarChangeBehaviour: forwardingOrderChangeBehavioursEnum.Update,
                                    }),
                                delete: () =>
                                    resolve({
                                        forwardingOrderCarChangeBehaviour: forwardingOrderChangeBehavioursEnum.Delete,
                                    }),
                                cancel: () => resolve(false),
                            },
                        });
                    } else {
                        resolve({});
                    }
                });
            }),
        );
    }

    if (promises.length === 0) {
        return Promise.resolve({});
    }

    const extraOptionsArr = [];
    const promisesSeries = async () => {
        for (let i = 0; i < promises.length; i++) {
            const data = await promises[i]();
            extraOptionsArr.push(data);
        }
    };
    return promisesSeries().then(() => {
        if (extraOptionsArr.some((item) => !item)) {
            return false;
        }
        return extraOptionsArr.reduce((acc, item) => ({ ...acc, ...item }), {});
    });
};

class BidItemChangeForm extends BookForm {
    state = {
        isLoading: true,
        isValid: true,
        bidsForParallelPoint: [],
    };

    componentDidMount() {
        this.parallelItemChangePointsEnabled =
            (parallelItemChangePointsFlagValue & this.props.organization.intFlags) ===
            parallelItemChangePointsFlagValue;
        this.setState({ isLoading: false, ...this.props.initialState });
    }

    isDraftMode = () => this.props.mode === "draft";

    getDraftItem = (state = this.state) => {
        const {
            bidsForParallelPoint,
            dataForVisualization,
            dataForVisualizationLoading,
            dataVisualizationKey,
            currentCar,
            currentDriver,
            currentSecondaryDriver,
            currentTrailer,
            showPlanLeaveDate,
            showSecondaryDriver,
            isLoading,
            isValid,
            ...item
        } = state;
        return { ...this.props.item, ...item };
    };

    syncDraftItem = (state = this.state) => {
        if (this.isDraftMode()) {
            this.props.onDraftChange?.(this.getDraftItem(state));
        }
    };

    applyReq = (state) => {
        const { bidId, hasParallelBidId, add, firstBidPointEntered } = this.props;
        const serialized = formatBidItemChangePoint(state);
        const req = (extraParams = {}) =>
            applyItemChangePoints({ ...extraParams, bidId, items: [serialized] }).then((res) => {
                if (res) {
                    this.close();
                    this.props.getItem && this.props.getItem();
                } else {
                    this.setState({ isLoading: false, isValid: false });
                }
            });

        return runBidItemChangePointChecks({
            add,
            bid: this.props.bid,
            bidId,
            firstBidPointEntered,
            hasParallelBidId,
            pointToApply: serialized,
        }).then((extraParams) => {
            if (!extraParams) {
                return this.setState({ isLoading: false });
            }
            return req(extraParams);
        });
    };

    isValidForm = (state) => {
        return isValidBidItemChangePoint(state);
    };

    setValue = ({ name, value, option }) => {
        const stateUpdate = { [name]: value };
        if (name === "showPlanLeaveDate" && !value) {
            stateUpdate.planLeaveDate = null;
        }
        if (name === "shiftCarId") {
            stateUpdate.shiftCar = option;
        }
        const newValue = { ...this.state, ...stateUpdate };
        const isValid = this.isValidForm(newValue);
        this.setState({ ...stateUpdate, ...(isValid ? { isValid: true } : {}) }, () => this.syncDraftItem());

        if (this.props.bidId && !this.props.hasParallelBidId && this.parallelItemChangePointsEnabled) {
            if (
                fieldsForParallelChange.includes(name) &&
                requiredFieldsForParallelChange.and.every((key) => !!newValue[key]) &&
                requiredFieldsForParallelChange.or.some((key) => !!newValue[key])
            ) {
                const parallelBidChangeData = formatBidItemChangePoint(newValue);
                delete parallelBidChangeData.id;
                if (parallelBidChangeData.planEnterDate) {
                    parallelBidChangeData.planEnterDate = moment(parallelBidChangeData.planEnterDate).format(
                        "YYYY-MM-DDTHH:mm",
                    );
                }
                if (parallelBidChangeData.planLeaveDate) {
                    parallelBidChangeData.planLeaveDate = moment(parallelBidChangeData.planLeaveDate).format(
                        "YYYY-MM-DDTHH:mm",
                    );
                }
                getBidsForParallelPoint({ bidId: this.props.bidId, point: parallelBidChangeData }).then(
                    (bidsForParallelPoint) => {
                        if (bidsForParallelPoint) {
                            this.setState({ bidsForParallelPoint });
                        }
                    },
                );
            }
        }
        if (this.props.bidId && fieldsForVisualization.includes(name)) {
            this.setState({ dataForVisualizationLoading: true });
            if (fieldsForVisualization.every((key) => !!newValue[key])) {
                getBidForItemChangePointVisualization(
                    this.props.bidId,
                    newValue.shiftCarId,
                    moment(newValue.planEnterDate).format("YYYY-MM-DDTHH:mm"),
                ).then((res) =>
                    this.setState({
                        dataForVisualization: res,
                        dataVisualizationKey: Date.now(),
                        dataForVisualizationLoading: false,
                    }),
                );
            } else {
                this.setState({
                    dataForVisualization: undefined,
                    dataVisualizationKey: Date.now(),
                    dataForVisualizationLoading: false,
                });
            }
        }
    };

    showSecondaryDriver = () => this.setState({ showSecondaryDriver: true });

    renderReplacementSelect = (props) =>
        this.renderSelect({
            ...props,
            bWrapper: "bid-item-change-form__select",
            labelWrapper: "bid-item-change-form__select-label",
            fieldWrapper: "bid-item-change-form__select-field",
        });

    renderReplacementBody = ({ add, bid, bidId, bidLastCarLocation, route }) => {
        const {
            bidsForParallelPoint,
            currentCar,
            currentDriver,
            currentSecondaryDriver,
            currentTrailer,
            dataForVisualization,
            dataForVisualizationLoading,
            dataVisualizationKey,
            parallelBidId,
            showPlanLeaveDate,
            showSecondaryDriver,
            shiftCarId,
            shiftDriverId,
            shiftSecondaryDriverId,
            shiftTrailerId,
        } = this.state;
        const excludedDriverIds = [currentDriver?.id, currentSecondaryDriver?.id].filter(Boolean);

        const switchMap = (params) => {
            if (this.isDraftMode() && !bidId && this.props.switchMap) {
                if (params) {
                    this.props.switchMap(params);
                    return;
                }
                this.props.switchMap({
                    mode: "picker",
                    value: this.state.mapObject,
                    callback: (value) => {
                        if (value && Object.keys(value).length) {
                            this.setValue({ name: "mapObject", value });
                        }
                        this.props.switchMap({ mode: "main", callback: null, withoutRadius: null });
                    },
                });
                return;
            }
            add({
                name: "shiftCarMapPicker",
                title: "Локация смены составляющих",
                windowMod: "wide modal--vh-75 map",
                route,
                bid,
                bidLastCarLocation,
                bidId,
                shiftCarId: this.state.shiftCarId,
                shiftCar: this.state.shiftCar,
                callback: (value) => {
                    if (value && Object.keys(value).length) {
                        this.setValue({ name: "mapObject", value });
                    }
                },
            });
        };

        return (
            <>
                <ReplacementForm
                    currentCar={currentCar}
                    currentDriver={currentDriver}
                    currentSecondaryDriver={currentSecondaryDriver}
                    currentTrailer={currentTrailer}
                    actualCurrentCar={bid.car || bid.carOption}
                    actualCurrentDriver={bid.driver}
                    actualCurrentSecondaryDriver={bid.secondaryDriver}
                    actualCurrentTrailer={bid.trailer || bid.trailerOption}
                    showSecondaryDriver={showSecondaryDriver}
                    onShowSecondaryDriver={this.showSecondaryDriver}
                    renderSelect={this.renderReplacementSelect}
                    renderCheckbox={this.renderCheckbox}
                    config={this.props.config}
                    excludedDriverIds={excludedDriverIds}
                    selectedCarId={shiftCarId}
                    selectedDriverId={shiftDriverId}
                    selectedSecondaryDriverId={shiftSecondaryDriverId}
                    selectedTrailerId={shiftTrailerId}
                    shouldClearSecondaryDriver={this.state.shouldClearSecondaryDriver}
                />
                <hr className="bid-item-change-form__divider" />
                <div className="bid-item-change-form__section-title">2. Где произойдет замена?</div>
                <AddressPicker
                    label="Адрес перецепки / пересменки"
                    mapObject={this.state.mapObject}
                    onChange={this.setValue}
                    addModal={add}
                    switchMap={switchMap}
                />
                <hr className="bid-item-change-form__divider" />
                <div className="bid-item-change-form__section-title">3. Когда произойдет замена?</div>
                <div className="bid-item-change-form__dates mb-3">
                    {this.renderDateField({
                        ...this.props.config.planEnterDate,
                        label: "Плановая дата и время въезда",
                        bWrapper: "bid-item-change-form__date-field",
                        labelWrapper: "bid-item-change-form__date-label",
                        wrapper: "bid-item-change-form__date-input",
                    })}
                    {showPlanLeaveDate
                        ? this.renderDateField({
                              ...this.props.config.planLeaveDate,
                              label: "Плановая дата и время выезда",
                              bWrapper: "bid-item-change-form__date-field",
                              labelWrapper: "bid-item-change-form__date-label",
                              wrapper: "bid-item-change-form__date-input",
                          })
                        : this.renderCheckbox({
                              label: "Указать плановую дату и время выезда",
                              inputProps: { name: "showPlanLeaveDate" },
                              bWrapper: "bid-item-change-form__leave-checkbox",
                              labelWrapper: "bid-item-change-form__leave-checkbox-label",
                              fieldWrapper: "bid-item-change-form__leave-checkbox-field",
                              fieldBeforeLabel: true,
                          })}
                </div>
                {parallelBidId && (
                    <div className="bid-item-change-form__parallel-bid">
                        <div className="bid-item-change-form__parallel-bid-icon">
                            <i className="icon-uEAB3-arrows-rotate" />
                        </div>
                        <div className="bid-item-change-form__parallel-bid-data">
                            <div className="bid-item-change-form__parallel-bid-label">Параллельная заявка</div>
                            <a
                                className="bid-item-change-form__parallel-bid-link"
                                href={paths.bidShow("bid", parallelBidId)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Заявка № {parallelBidId}
                                <i className="icon-uEA96-external-link" />
                            </a>
                        </div>
                    </div>
                )}
                {bidsForParallelPoint.length > 0 && (
                    <BidsForParallelPoint
                        items={bidsForParallelPoint}
                        parallelBidId={parallelBidId}
                        selectPoint={(value) => this.setValue({ name: "parallelBidId", value })}
                    />
                )}
                {dataForVisualizationLoading && <Loader />}
                {dataForVisualization && !dataForVisualizationLoading && (
                    <Suspense fallback={<div className="loading" />}>
                        <TimelineForVisualization
                            key={dataVisualizationKey}
                            data={dataForVisualization}
                            shiftCar={this.state.shiftCar}
                            currentCar={bid.car}
                            bidPoints={bid.bidPoints}
                            estimations={bid.estimations || []}
                            itemChangeDate={this.state.planEnterDate}
                        />
                    </Suspense>
                )}
            </>
        );
    };

    render() {
        const { add, route, bid, bidLastCarLocation, bidId } = this.props;

        const { isLoading } = this.state;
        if (isLoading) {
            return (
                <div className="loader-wrap">
                    <Loader prefix="absolute-center" />
                </div>
            );
        }
        const cname = cn({ "book-form__body": !this.isDraftMode(), "book-form__body--invalid": !this.state.isValid });
        const body = this.renderReplacementBody({ add, bid, bidId, bidLastCarLocation, route });

        if (this.isDraftMode()) {
            return <div className={cname}>{body}</div>;
        }

        return (
            <form className={cname} onSubmit={this.onSubmit}>
                {body}
                <div className="mt-3 float-right">
                    <div className="btn btn-sm btn-secondary mr-2" onClick={this.props.back}>
                        Отмена
                    </div>
                    <input type="submit" className="btn btn-sm btn-brand" value="Сохранить" />
                </div>
            </form>
        );
    }
}

const ConfiguredForm = (props) => {
    const initialData = pick(props.item, Object.keys(config));
    const selectedResourceIds = {
        shiftCarId: props.item?.shiftCarId ?? getResourceId(props.item?.shiftCar),
        shiftDriverId: props.item?.shiftDriverId ?? getResourceId(props.item?.shiftDriver),
        shiftSecondaryDriverId: props.item?.shiftSecondaryDriverId ?? getResourceId(props.item?.shiftSecondaryDriver),
        shiftTrailerId: props.item?.shiftTrailerId ?? getResourceId(props.item?.shiftTrailer),
    };
    const composition = resolveCurrentComposition(props.bid, props.items, props.item, props.itemChangesHistory);
    const shouldClearSecondaryDriver = hasClearSecondaryDriverFlag(props.item);
    return (
        <BidItemChangeForm
            {...props}
            config={config}
            initialState={{
                ...initialState,
                ...initialData,
                ...selectedResourceIds,
                mapObject: initialData.mapObject || null,
                shouldClearSecondaryDriver,
                currentCar: composition.car,
                currentDriver: composition.driver,
                currentSecondaryDriver: composition.secondaryDriver,
                currentTrailer: composition.trailer,
                showPlanLeaveDate: Boolean(initialData.planLeaveDate),
                showSecondaryDriver: Boolean(composition.secondaryDriver || selectedResourceIds.shiftSecondaryDriverId),
            }}
        />
    );
};

export default ConfiguredForm;
